import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';
import type {
  PlatformAlertDelivery,
  PlatformAlertDeliveryStatus,
  PlatformMessaging,
} from '@neuroedge/shared';
import { Button, Card, PageState, TextField, useToast } from '../../components/ui';
import { DataGrid, useGridState, type GridColumn } from '../../components/data-grid/DataGrid';
import { useAuth } from '../../auth/AuthContext';
import { fieldErrorsFromApiError } from '../../lib/field-errors';
import {
  useAlertDeliveries,
  useCreateAlertTemplates,
  usePlatformMessaging,
  usePlatformMessagingOptions,
  useSavePlatformMessaging,
  useSendTestAlert,
} from '../../api/platform-alerts';

const muted = { color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' } as const;

const TEMPLATE_TONE: Record<string, string> = {
  APPROVED: 'var(--color-success)',
  REJECTED: 'var(--color-danger)',
  PAUSED: 'var(--color-danger)',
  DISABLED: 'var(--color-danger)',
};

interface FormState {
  alertsTenantId: string;
  alertsChannelId: string;
  alertsLanguage: string;
  whatsappAlertsEnabled: boolean;
  emailFallbackEnabled: boolean;
  disabledAlerts: string[];
}

const toForm = (s: PlatformMessaging): FormState => ({
  alertsTenantId: s.alertsTenantId ?? '',
  alertsChannelId: s.alertsChannelId ?? '',
  alertsLanguage: s.alertsLanguage,
  whatsappAlertsEnabled: s.whatsappAlertsEnabled,
  emailFallbackEnabled: s.emailFallbackEnabled,
  disabledAlerts: s.alerts.filter((a) => !a.enabled).map((a) => a.type),
});

function validate(f: FormState): Record<string, string> {
  const errors: Record<string, string> = {};
  if (f.alertsChannelId && !f.alertsTenantId) {
    errors.alertsTenantId = 'Choose the platform workspace.';
  }
  if (f.whatsappAlertsEnabled && !f.alertsChannelId) {
    errors.alertsChannelId = 'Pick the WhatsApp number that sends alerts.';
  }
  if (!/^[a-z]{2,3}(_[A-Z]{2})?$/.test(f.alertsLanguage.trim())) {
    errors.alertsLanguage = 'Use a Meta language code, e.g. en or en_US.';
  }
  return errors;
}

/**
 * Admin → Platform messaging: which workspace and WhatsApp number send
 * account and billing alerts to every workspace's owners/admins, which
 * alerts are on, creating their templates in Meta, a test send and the
 * delivery log (docs/PLATFORM_WHATSAPP_ALERTS.md).
 */
export function PlatformMessagingPage() {
  const { isPlatformAdmin } = useAuth();
  const settings = usePlatformMessaging();
  const options = usePlatformMessagingOptions();
  const save = useSavePlatformMessaging();
  const createTemplates = useCreateAlertTemplates();
  const sendTest = useSendTestAlert();
  const toast = useToast();
  const [form, setForm] = useState<FormState | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [testType, setTestType] = useState('invoice_due_reminder');

  useEffect(() => {
    if (settings.data && !form) setForm(toForm(settings.data));
  }, [settings.data, form]);

  if (!isPlatformAdmin) return <Navigate to="/" replace />;
  if (settings.isPending || !form) return <PageState kind="loading" />;
  if (settings.isError) {
    return (
      <PageState
        kind="error"
        message={settings.error.message}
        onRetry={() => void settings.refetch()}
      />
    );
  }

  const data = settings.data;
  const channels = (options.data?.channels ?? []).filter((c) => c.tenantId === form.alertsTenantId);
  const set = (patch: Partial<FormState>) => {
    setForm({ ...form, ...patch });
    setFieldErrors((e) => {
      const next = { ...e };
      for (const k of Object.keys(patch)) delete next[k];
      return next;
    });
  };
  const savedChannel = data.alertsChannelId !== null;

  async function onSave(e: FormEvent) {
    e.preventDefault();
    const errors = validate(form!);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    try {
      const saved = await save.mutateAsync({
        alertsTenantId: form!.alertsTenantId || null,
        alertsChannelId: form!.alertsChannelId || null,
        alertsLanguage: form!.alertsLanguage.trim(),
        whatsappAlertsEnabled: form!.whatsappAlertsEnabled,
        emailFallbackEnabled: form!.emailFallbackEnabled,
        disabledAlerts: form!.disabledAlerts,
      });
      setForm(toForm(saved));
      toast.success('Platform messaging saved.');
    } catch (err) {
      const fields = fieldErrorsFromApiError(err);
      setFieldErrors(fields);
      toast.error(err instanceof Error ? err.message : 'Could not save.');
    }
  }

  async function onCreateTemplates() {
    try {
      const r = await createTemplates.mutateAsync();
      if (r.failed.length > 0) {
        toast.error(
          `${r.created.length} submitted, ${r.failed.length} failed: ${r.failed[0]!.name} - ${r.failed[0]!.error}`,
        );
      } else {
        toast.success(
          r.created.length > 0
            ? `${r.created.length} templates submitted to Meta for review.`
            : 'All alert templates already exist on this number.',
        );
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not create the templates.');
    }
  }

  async function onSendTest() {
    try {
      const d = await sendTest.mutateAsync(testType);
      if (d.status === 'SENT') {
        toast.success(
          d.channel === 'WHATSAPP'
            ? `Sent to your WhatsApp ${d.destination}.`
            : `Sent by email to ${d.destination}${d.error ? ` (${d.error})` : ''}.`,
        );
      } else {
        toast.error(d.error ?? `Not sent (${d.status.toLowerCase()}).`);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not send the test.');
    }
  }

  const toggleAlert = (type: string, on: boolean) =>
    set({
      disabledAlerts: on
        ? form.disabledAlerts.filter((t) => t !== type)
        : [...form.disabledAlerts, type],
    });

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 1100 }}>
      <div>
        <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>Platform messaging</h1>
        <p style={{ margin: 'var(--space-1) 0 0', ...muted }}>
          Account and billing alerts to every workspace&apos;s owners and admins - on WhatsApp from
          the platform number for those who opted in on their Profile, otherwise by email. Always
          shown in the app as well.
        </p>
      </div>

      <form
        onSubmit={(e) => void onSave(e)}
        noValidate
        style={{ display: 'grid', gap: 'var(--space-4)' }}
      >
        <Card title="Sender">
          <div style={{ display: 'grid', gap: 'var(--space-3)', maxWidth: 560 }}>
            <div className="ne-field">
              <label className="ne-field__label" htmlFor="pm-tenant">
                Platform workspace
              </label>
              <select
                id="pm-tenant"
                className="ne-input"
                value={form.alertsTenantId}
                onChange={(e) => set({ alertsTenantId: e.target.value, alertsChannelId: '' })}
              >
                <option value="">Not set</option>
                {(options.data?.tenants ?? []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              {fieldErrors.alertsTenantId ? (
                <span className="ne-field__error">{fieldErrors.alertsTenantId}</span>
              ) : (
                <span style={muted}>
                  Use your own company&apos;s workspace - replies to alerts land in its Inbox.
                </span>
              )}
            </div>
            <div className="ne-field">
              <label className="ne-field__label" htmlFor="pm-channel">
                WhatsApp number that sends alerts
              </label>
              <select
                id="pm-channel"
                className="ne-input"
                value={form.alertsChannelId}
                disabled={!form.alertsTenantId}
                onChange={(e) => set({ alertsChannelId: e.target.value })}
              >
                <option value="">Not set</option>
                {channels.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                    {c.phone ? ` (${c.phone})` : ''}
                    {c.status === 'DISCONNECTED' ? ' - disconnected' : ''}
                  </option>
                ))}
              </select>
              {fieldErrors.alertsChannelId ? (
                <span className="ne-field__error">{fieldErrors.alertsChannelId}</span>
              ) : null}
            </div>
            <TextField
              label="Template language"
              value={form.alertsLanguage}
              onChange={(e) => set({ alertsLanguage: e.target.value })}
              error={fieldErrors.alertsLanguage}
            />
            <label style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={form.whatsappAlertsEnabled}
                onChange={(e) => set({ whatsappAlertsEnabled: e.target.checked })}
              />
              Send alerts on WhatsApp
            </label>
            <label style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={form.emailFallbackEnabled}
                onChange={(e) => set({ emailFallbackEnabled: e.target.checked })}
              />
              Email them when WhatsApp can&apos;t be used (not opted in, template not approved yet)
            </label>
          </div>
        </Card>

        <Card title="Alerts">
          <div
            style={{
              display: 'flex',
              gap: 'var(--space-2)',
              flexWrap: 'wrap',
              marginBottom: 'var(--space-3)',
            }}
          >
            <Button
              type="button"
              variant="secondary"
              disabled={!savedChannel || createTemplates.isPending}
              onClick={() => void onCreateTemplates()}
              title={savedChannel ? undefined : 'Save the WhatsApp number first'}
            >
              {createTemplates.isPending ? 'Submitting…' : 'Create templates in Meta'}
            </Button>
            <select
              className="ne-input"
              style={{ maxWidth: 280 }}
              aria-label="Alert to test"
              value={testType}
              onChange={(e) => setTestType(e.target.value)}
            >
              {data.alerts.map((a) => (
                <option key={a.type} value={a.type}>
                  {a.label}
                </option>
              ))}
            </select>
            <Button
              type="button"
              variant="secondary"
              disabled={sendTest.isPending}
              onClick={() => void onSendTest()}
            >
              {sendTest.isPending ? 'Sending…' : 'Send test to me'}
            </Button>
          </div>
          <p style={{ margin: '0 0 var(--space-3)', ...muted }}>
            Templates are named after the alert and need Meta&apos;s approval (usually minutes to a
            day). Use &quot;Sync from Meta&quot; on the platform workspace&apos;s Channels page to
            refresh their status. Until a template is approved, that alert goes by email.
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table className="ne-table" style={{ minWidth: 640 }}>
              <thead>
                <tr>
                  <th style={{ width: 60 }}>On</th>
                  <th>Alert</th>
                  <th>Template</th>
                  <th>Email fallback</th>
                </tr>
              </thead>
              <tbody>
                {data.alerts.map((a) => (
                  <tr key={a.type}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`${a.label} on`}
                        checked={!form.disabledAlerts.includes(a.type)}
                        onChange={(e) => toggleAlert(a.type, e.target.checked)}
                      />
                    </td>
                    <td>
                      <strong>{a.label}</strong>
                      <div style={{ ...muted, overflowWrap: 'anywhere' }}>{a.bodyText}</div>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <code style={muted}>{a.type}</code>
                      <div>
                        <span
                          className="ne-badge"
                          style={{
                            color: a.templateStatus ? TEMPLATE_TONE[a.templateStatus] : undefined,
                          }}
                        >
                          {a.templateStatus ?? 'Not created'}
                        </span>
                      </div>
                    </td>
                    <td>{a.emailFallback ? 'Yes' : 'No - already emailed'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <div>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save settings'}
          </Button>
        </div>
      </form>

      <DeliveryLog />

      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/admin/dashboard">Admin dashboard</Link>
      </p>
    </div>
  );
}

const STATUS_TABS: { value: PlatformAlertDeliveryStatus | 'ALL'; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'SENT', label: 'Sent' },
  { value: 'QUEUED', label: 'Queued' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'SKIPPED', label: 'Skipped' },
];

const STATUS_TONE: Record<PlatformAlertDeliveryStatus, string | undefined> = {
  SENT: 'var(--color-success)',
  QUEUED: undefined,
  FAILED: 'var(--color-danger)',
  SKIPPED: 'var(--color-text-muted)',
};

function DeliveryLog() {
  const [tab, setTab] = useState<PlatformAlertDeliveryStatus | 'ALL'>('ALL');
  const [grid, setGrid] = useGridState({ sortBy: 'createdAt', sortOrder: 'desc' });
  const deliveries = useAlertDeliveries({
    status: tab === 'ALL' ? undefined : tab,
    search: grid.filters.recipient?.trim() || undefined,
    page: grid.page,
    pageSize: grid.pageSize,
  });

  const columns: GridColumn<PlatformAlertDelivery>[] = [
    {
      key: 'createdAt',
      header: 'When',
      render: (d) => (
        <span style={{ whiteSpace: 'nowrap' }}>{new Date(d.createdAt).toLocaleString()}</span>
      ),
    },
    { key: 'alertType', header: 'Alert', render: (d) => <code>{d.alertType}</code> },
    {
      key: 'recipient',
      header: 'To',
      filter: { type: 'text', placeholder: 'Workspace, person, number or alert' },
      render: (d) => (
        <div>
          {d.recipientName} · {d.tenantName}
          <div style={muted}>
            {d.channel === 'WHATSAPP' ? 'WhatsApp' : 'Email'} {d.destination}
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (d) => (
        <div>
          <span className="ne-badge" style={{ color: STATUS_TONE[d.status] }}>
            {d.status}
          </span>
          {d.error ? <div style={{ ...muted, overflowWrap: 'anywhere' }}>{d.error}</div> : null}
        </div>
      ),
    },
  ];

  return (
    <Card title="Delivery log">
      <div role="tablist" aria-label="Delivery status" className="ne-tabs">
        {STATUS_TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            role="tab"
            aria-selected={tab === t.value}
            className="ne-tabs__tab"
            onClick={() => {
              setTab(t.value);
              setGrid({ ...grid, page: 1 });
            }}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div style={{ marginTop: 'var(--space-3)' }}>
        {deliveries.isError ? (
          <PageState
            kind="error"
            message={deliveries.error.message}
            onRetry={() => void deliveries.refetch()}
          />
        ) : (
          <DataGrid
            columns={columns}
            rows={deliveries.data?.items ?? []}
            rowKey={(d) => d.id}
            rowLabel={(d) => `${d.alertType} to ${d.recipientName}`}
            total={deliveries.data?.meta.total ?? 0}
            state={grid}
            onStateChange={setGrid}
            loading={deliveries.isFetching}
            emptyMessage="No alerts sent yet."
          />
        )}
      </div>
    </Card>
  );
}
